// CONTRACT:I4-POLICY-DECISION.1.0
/** The high-level PDP entry point the COP calls: derive an object's bound FQNs
 *  from its security labels, evaluate against the subject, and map to the two-state
 *  presentation. One call per object per clearance change. */

import type { Decision, Presentation, PolicyBundle, SubjectClaims } from "./types.ts";
import { evaluateAbac } from "./evaluate.ts";
import { deriveBoundFqns, type SecurityView } from "./fqn.ts";
import { validateMarkings, type MarkingValidation } from "./validate.ts";

/** Is this object's payload cryptographically protected (gets the 🔒 badge when
 *  visible)? An encrypted payload mode or any non-`clear` leakage profile. */
function isProtected(security: SecurityView & { payload_mode?: string; leakage_profile?: string }): boolean {
  return security.payload_mode === "encrypted" || (security.leakage_profile != null && security.leakage_profile !== "clear");
}

/** Map a decision + the object's protection state to the TWO-STATE presentation
 *  (AOA §3, amended): permit → show (+ badge if protected); deny → hidden. */
export function present(
  decision: Decision,
  security: SecurityView & { payload_mode?: string; leakage_profile?: string },
): Presentation {
  if (!decision.permit) return { visible: false, protectedBadge: false };
  return { visible: true, protectedBadge: isProtected(security) };
}

export interface VisibilityResult {
  decision: Decision;
  presentation: Presentation;
  /** The bound attribute FQNs the decision was made over (for the (i) inspector). */
  boundFqns: string[];
  /** Marking-coherence check (independent of the subject). */
  validation: MarkingValidation;
}

/** Decide visibility for one object under one subject. Pure; safe to call over the
 *  whole picture on every clearance change.
 *
 *  FAIL-CLOSED on invalid markings: an object whose markings are internally
 *  CONTRADICTORY (e.g. NOFORN + REL TO GBR) is never shown in the clear regardless
 *  of clearance — it is hidden and flagged. (Warnings do not block visibility.) */
export function decideVisibility(
  security: SecurityView & { payload_mode?: string; leakage_profile?: string },
  bundle: PolicyBundle,
  claims: SubjectClaims,
): VisibilityResult {
  const validation = validateMarkings(security, bundle);
  const boundFqns = deriveBoundFqns(security, bundle);

  if (!validation.valid) {
    const errs = validation.violations.filter((v) => v.severity === "error").map((v) => v.rule);
    const decision: Decision = {
      permit: false,
      reason: `invalid markings (fail-closed): ${errs.join(", ")}`,
      verdicts: [],
    };
    return { decision, presentation: { visible: false, protectedBadge: false }, boundFqns, validation };
  }

  const decision = evaluateAbac(boundFqns, bundle, claims);
  return { decision, presentation: present(decision, security), boundFqns, validation };
}
